import React, { useState, useEffect } from "react";
import { fetchApi } from "../../utils/api";
import { useToast } from "../../components/ui/use-toast";
import "../../style/user/CampaignList.css";
import CampaignInfo from "./CampaignInfo";
import Modal from "../../components/Modal";

export default function CampaignList({ onUploadNumbers, onCampaignUpdate }) {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 10;
  const { addToast } = useToast();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleRowClick = async (campaignId) => {
    try {
      const response = await fetchApi(`/campaigns/${campaignId}`);
      const data = response?.data || response;

      setSelectedCampaign(data);
      setShowModal(true);
    } catch (err) {
      addToast({
        title: "Error",
        description: err.message || "Failed to load campaign details",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await fetchApi("/campaigns");
      const data = Array.isArray(response) ? response : response?.data ? response.data : [];

      const validatedData = data.map((campaign) => ({
        id: campaign.id || "",
        name: campaign.name || campaign.campaign_name || "",
        channel: campaign.channel || campaign.campaign_type || "",
        status: campaign.status || "",
        created_at: campaign.created_at || new Date().toISOString(),
      }));

      setCampaigns(validatedData);
    } catch (err) {
      addToast({
        title: "Error",
        description: err.message || "Failed to load campaigns",
        variant: "error",
      });
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) {
      return;
    }
    try {
      await fetchApi(`/campaigns/${campaignId}`, { method: "DELETE" });
      addToast({
        title: "Success",
        description: "Campaign deleted successfully",
        variant: "default",
      });
      fetchCampaigns();
      if (onCampaignUpdate) onCampaignUpdate();
    } catch (error) {
      addToast({
        title: "Error",
        description: error.message || "Failed to delete campaign",
        variant: "destructive",
      });
    }
  };

  // Fungsi untuk mapping status dari API ke value filter
  const getFilterStatusValue = (apiStatus) => {
    switch (apiStatus) {
      case "on_process":
        return "on_process";
      case "success":
      case "completed":
        return "completed";
      case "failed":
        return "failed";
      default:
        return apiStatus;
    }
  };

  // Fungsi untuk mendapatkan display text status
  const getStatusDisplayText = (status) => {
    switch (status) {
      case "on_process":
        return "Checking Campaign";
      case "success":
      case "completed":
        return "Campaign Success";
      case "failed":
        return "Campaign Failed";
      default:
        return status;
    }
  };

  // Fungsi untuk mendapatkan class badge status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "on_process":
        return "status-checking";
      case "success":
      case "completed":
        return "status-success";
      case "failed":
        return "status-failed";
      default:
        return "";
    }
  };

  const filteredData = campaigns.filter((c) => {
    const nameMatch = c.name?.toLowerCase()?.includes(search.toLowerCase()) ?? false;
    const idMatch = c.id?.toString()?.includes(search) ?? false;
    const channelMatch = channelFilter ? c.channel === channelFilter : true;
    
    // Status filter - handle both 'success' and 'completed'
    let statusMatch = true;
    if (statusFilter) {
      if (statusFilter === "completed") {
        statusMatch = c.status === "success" || c.status === "completed";
      } else {
        statusMatch = c.status === statusFilter;
      }
    }
    
    const monthMatch = monthFilter ? new Date(c.created_at).getMonth() + 1 === parseInt(monthFilter) : true;

    return (nameMatch || idMatch) && channelMatch && statusMatch && monthMatch;
  });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page) => setCurrentPage(page);

  if (loading) {
    return <div className="loading">Loading campaigns...</div>;
  }

  return (
    <div className="campaign-container-list">
      <h3 className="title">Campaign Activity</h3>

      {/* Search Box */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search campaign by campaign name or id campaign.."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Filters */}
      <div className="filters-campaign">
        <select
          className="filter-select-campaign"
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
        >
          <option value="">Channel</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="sms">SMS</option>
        </select>

        <select
          className="filter-select-campaign"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Status</option>
          <option value="on_process">Checking Campaign</option>
          <option value="completed">Campaign Success</option>
          <option value="failed">Campaign Failed</option>
        </select>

        <select
          className="filter-select-campaign"
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <option value="">Month</option>
          {[...Array(12)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(0, i).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="table-wrapper-cpUser">
        <table className="campaign-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>ID Campaign</th>
              <th>Campaign Name</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Campaign Date</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((c, index) => (
                <tr key={c.id} onClick={() => handleRowClick(c.id)} className="clickable-row">
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td>{Math.floor(new Date(c.created_at).getTime() / 1000)}</td>
                  <td>{c.name}</td>
                  <td className="capitalize">{c.channel}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(c.status)}`}>
                      {getStatusDisplayText(c.status)}
                    </span>
                  </td>
                  <td>
                    {new Date(c.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  No campaigns found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="table-footer">
        <div className="text-sm">
          {paginatedData.length} out of {filteredData.length} data
        </div>
        <div className="pagination-campaign">
          <button
            className="page-btn"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) pageNum = i + 1;
            else if (currentPage <= 3) pageNum = i + 1;
            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = currentPage - 2 + i;

            return (
              <button
                key={pageNum}
                className={`page-btn ${pageNum === currentPage ? "active" : ""}`}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            className="page-btn"
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      </div>

{showModal && selectedCampaign && (
  <Modal onClose={() => setShowModal(false)}>
    <CampaignInfo 
      campaign={selectedCampaign} 
      onClose={() => setShowModal(false)}  // Tambahkan ini
    />
  </Modal>
)}
    </div>
  );
}